import { PropsWithChildren } from "react";
import { IonSpinner } from "@ionic/react";

import styles from "./Button.module.css";

type ButtonProps = PropsWithChildren & {
  type: "PRIMARY" | "SECONDARY" | "TERTIARY";
  label?: string;
  onClick: () => void;
  /**
   * When true, the button will be 100% width
   * @default false
   */
  isFluid?: boolean;
  isDisabled?: boolean;
  /**
   * When true, a spinner will be shown in the button
   * @default false
   */
  isActionPending?: boolean;
  // TODO add 'iconLeft?: IconType' back when the new icons have been added as assets
};

/**
 * @deprecated Use the `Button` component from `swing-components` instead.
 */
export function Button(props: ButtonProps) {
  const { type, ...otherProps } = {
    ...props,
    isFluid: props.isFluid ?? false,
  };

  return (
    <>
      {type === "PRIMARY" && <PrimaryButton {...otherProps} />}
      {type === "SECONDARY" && <SecondaryButton {...otherProps} />}
      {type === "TERTIARY" && <TertiaryButton {...otherProps} />}
    </>
  );
}

function PrimaryButton(props: Omit<ButtonProps, "type">) {
  const { label, isFluid, onClick, isDisabled = false, isActionPending = false, children } = props;

  return (
    <button
      aria-label={label}
      className={`${styles.swingButton} ${styles.swingButtonPrimary} ${
        isFluid ? styles.swingButtonFluid : ""
      } `}
      disabled={isDisabled}
      onClick={onClick}
    >
      <span css={{ display: "inline-flex", alignItems: "center" }}>
        {isActionPending && <Spinner />}
        {children && (
          <span
            className={
              isDisabled
                ? `${styles.swingButtonPrimaryIconDisabled}`
                : `${styles.swingButtonPrimaryIcon}`
            }
          >
            {children}
          </span>
        )}
        {label}
      </span>
    </button>
  );
}

function SecondaryButton(props: Omit<ButtonProps, "type">) {
  const { label, isFluid, onClick, isActionPending = false, isDisabled = false, children } = props;

  return (
    <button
      aria-label={label}
      className={`${styles.swingButton} ${styles.swingButtonSecondary} ${
        isFluid ? styles.swingButtonFluid : ""
      } `}
      disabled={isDisabled}
      onClick={onClick}
    >
      <span css={{ display: "inline-flex", alignItems: "center" }}>
        {isActionPending && <Spinner />}
        {children && (
          <span
            className={
              isDisabled
                ? `${styles.swingButtonSecondaryIconDisabled}`
                : `${styles.swingButtonSecondaryIcon}`
            }
          >
            {children}
          </span>
        )}
        {label}
      </span>
    </button>
  );
}

function TertiaryButton(props: Omit<ButtonProps, "type">) {
  const { label, isFluid, onClick, isActionPending = false, isDisabled = false, children } = props;

  return (
    <button
      aria-label={label}
      className={`${styles.swingButton} ${styles.swingButtonTertiary} ${
        isFluid ? styles.swingButtonFluid : ""
      } `}
      disabled={isDisabled}
      onClick={onClick}
    >
      <span css={{ display: "inline-flex", alignItems: "center" }}>
        {isActionPending && <Spinner />}
        {children && (
          <span
            className={
              isDisabled
                ? `${styles.swingButtonTertiaryIconDisabled}`
                : `${styles.swingButtonTertiaryIcon}`
            }
          >
            {children}
          </span>
        )}
        {label}
      </span>
    </button>
  );
}

/********* Helper Component ********/
function Spinner() {
  return (
    <IonSpinner
      name="circular"
      css={{
        margin: "-5px 10px -5px -10px",
        opacity: "0.5",
        transition: "visibility 0.5s ease-in, opacity 0.5s ease-in",
        width: 24,
        height: 24,
      }}
    />
  );
}
